import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { moviesData } from '@/mocks/movies';
import { getByType, typeLabel } from '@/utils/movie';
import { useSeo } from '@/hooks/useSeo';
import MovieCard from '@/components/base/MovieCard';
import FilterBar from '@/pages/category/components/FilterBar';

const PAGE_SIZE = 12;

const typeInfo: Record<string, { desc: string; seoDesc: string }> = {
  tv: {
    desc: '电视剧频道，涵盖最新热播剧集与口碑佳作',
    seoDesc: '牛牛视频电视剧频道，聚合最新热播剧集与高分口碑电视剧，支持按题材、地区、年份筛选。',
  },
  movie: {
    desc: '电影频道，精选高口碑电影与院线佳作',
    seoDesc: '牛牛视频电影频道，精选高口碑电影与院线佳作，支持按类型、地区、年份筛选。',
  },
  variety: {
    desc: '综艺频道，欢乐下饭的综艺节目合集',
    seoDesc: '牛牛视频综艺频道，聚合热门综艺节目与欢乐下饭内容，支持按类型、地区、年份筛选。',
  },
};

const sortOptions = [
  { label: '最新', value: '最新' },
  { label: '最热', value: '最热' },
  { label: '评分', value: '评分' },
];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const validType = type && typeInfo[type] ? type : null;

  const [category, setCategory] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('最新');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const base = useMemo(() => (validType ? getByType(moviesData, validType) : []), [validType]);

  const categoryOptions = useMemo(() => {
    const set = Array.from(new Set(base.map((m) => m.category)));
    return [{ label: '全部', value: '全部' }, ...set.map((c) => ({ label: c, value: c }))];
  }, [base]);

  const regionOptions = useMemo(() => {
    const set = Array.from(new Set(base.map((m) => m.region)));
    return [{ label: '全部', value: '全部' }, ...set.map((r) => ({ label: r, value: r }))];
  }, [base]);

  const yearOptions = useMemo(() => {
    const set = Array.from(new Set(base.map((m) => m.year))).sort((a, b) => b - a);
    return [{ label: '全部', value: '全部' }, ...set.map((y) => ({ label: `${y}`, value: `${y}` }))];
  }, [base]);

  // 切换分类时重置全部筛选
  useEffect(() => {
    setCategory('全部');
    setRegion('全部');
    setYear('全部');
    setSort('最新');
    setPage(1);
    setFiltersOpen(false);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [category, region, year, sort]);

  const filtered = useMemo(() => {
    const list = base.filter(
      (m) =>
        (category === '全部' || m.category === category) &&
        (region === '全部' || m.region === region) &&
        (year === '全部' || `${m.year}` === year),
    );
    return list.sort((a, b) => {
      if (sort === '最热') return b.views - a.views;
      if (sort === '评分') return b.rating - a.rating;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [base, category, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const label = validType ? typeLabel(validType) : '影视';
  const info = validType ? typeInfo[validType] : null;

  useSeo({
    title: `${label}频道 - 牛牛视频`,
    description: info?.seoDesc ?? '牛牛视频影视分类频道，聚合热门电视剧、电影与综艺内容。',
    keywords: `${label},牛牛视频,${label}推荐,在线观看`,
  });

  const changePage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetAll = () => {
    setCategory('全部');
    setRegion('全部');
    setYear('全部');
    setSort('最新');
  };

  if (!validType) {
    return (
      <div className="w-full px-4 md:px-8 pt-28 md:pt-32 pb-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-3xl text-foreground-400" />
        </span>
        <h1 className="mt-5 text-xl md:text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">请从下方频道入口重新选择一个分类</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/category/tv"
            className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            电视剧频道
          </Link>
          <Link
            to="/"
            className="h-10 px-5 inline-flex items-center rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const filterPanel = (
    <div className="space-y-4 md:space-y-5">
      <FilterBar title="题材" options={categoryOptions} value={category} onChange={setCategory} />
      <FilterBar title="地区" options={regionOptions} value={region} onChange={setRegion} />
      <FilterBar title="年份" options={yearOptions} value={year} onChange={setYear} />
      <FilterBar title="排序" options={sortOptions} value={sort} onChange={setSort} />
    </div>
  );

  return (
    <div className="w-full pt-16 md:pt-20">
      {/* 分类头部 */}
      <div className="w-full bg-primary-500">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200">
            <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{label}</span>
          </nav>
          <h1 className="mt-3 text-2xl md:text-4xl font-black text-background-50">{label}频道</h1>
          <p className="mt-2 text-sm md:text-base text-primary-100">{info?.desc}</p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {/* 筛选工具条 */}
        <div className="md:hidden mb-4">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="w-full h-11 flex items-center justify-between px-4 rounded-lg bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-filter-3-line" />
              筛选条件
            </span>
            <i className={filtersOpen ? 'ri-arrow-up-s-line text-lg' : 'ri-arrow-down-s-line text-lg'} />
          </button>
          {filtersOpen && <div className="mt-4 p-4 rounded-lg bg-background-100 border border-background-200/70">{filterPanel}</div>}
        </div>

        {/* 桌面筛选 */}
        <div className="hidden md:block p-5 lg:p-6 rounded-lg bg-background-100 border border-background-200/70">
          {filterPanel}
        </div>

        {/* 结果统计 */}
        <div className="flex items-center justify-between mt-6 md:mt-8 mb-5">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 部作品
          </p>
        </div>

        {/* 列表 */}
        {pageItems.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="py-16 md:py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-search-eye-line text-3xl text-foreground-400" />
            </span>
            <h3 className="mt-5 text-lg font-bold text-foreground-900">没有符合条件的影视</h3>
            <p className="mt-2 text-sm text-foreground-500">试试调整或重置筛选条件</p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-6 h-10 px-5 inline-flex items-center gap-2 rounded-md bg-accent-500 hover:bg-accent-600 text-accent-950 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          </div>
        )}

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="mt-8 md:mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => changePage(page - 1)}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-background-100 border border-background-200/70 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => changePage(p)}
                className={`h-9 min-w-9 px-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  p === page
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => changePage(page + 1)}
              className="h-9 w-9 flex items-center justify-center rounded-md bg-background-100 border border-background-200/70 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-background-200 transition-colors cursor-pointer"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}